import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePreviaPisCofins } from "../PreviaPisCofinsContext";
import Loading from "../../../components/Loading";
import SnackBar from "../../../components/SnackBar";
import WhiteButton from "../../../components/WhiteButton";
import BlueButton from "../../../components/BlueButton";
import { formatValue } from "../../../utils/formatValue";
import { buscarServicoPorTipo } from "../../../services/api-planejamento-tributario.service";
import { buscarValoresResumo, buscarPreviaVersaoPisCofins, iniciarProjeto, aprovarProjetoPisCofins, reenviarCreditosPisCofins, buscarTotaisCompoeConciliacaoSped, exportarCsvPreviaPisCofins } from "../../../services/api-piscofins.service";
import AvisoCreditosPendentes from "../../../components/PisCofins/AvisoCreditosPendentes";
import { exportarResumoGeralExcel } from "../../../utils/exportarResumoGeralExcel";
import { exportarResumoGeralPDF } from "../../../utils/exportarResumoGeralPDF";
import styles from "./styles.module.css";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import {
  usePermissoes,
  CHAVES_PIS_COFINS_EDIT,
  CHAVES_PIS_COFINS_APPROVE,
  CHAVES_PIS_COFINS_EXPORT,
} from "../../../config/Context/PermissoesContext";

const ResumoGeral = () => {
  const { pode } = usePermissoes();
  const navigate = useNavigate();
  const { versaoId } = useParams();
  const { dadosVersao, previaInfo, voltarParaListaComModalVersoes } = usePreviaPisCofins();

  const [loading, setLoading] = useState(false);
  const [loadingFinalizar, setLoadingFinalizar] = useState(false);
  const [dadosResumo, setDadosResumo] = useState(null);
  const [dadosVersaoCompleta, setDadosVersaoCompleta] = useState(null);
  const [tiposPrevia, setTiposPrevia] = useState([]);
  const [valoresSalvosPorScript, setValoresSalvosPorScript] = useState({});
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [creditosPendentes, setCreditosPendentes] = useState(null);

  const versaoIdFinal = Number(versaoId) || dadosVersao?.versaoId;

  const formatCurrency = (valor = 0) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);

  const formatarCnpj = (cnpj) => {
    if (!cnpj) return "Não informado";
    const cnpjLimpo = cnpj.replace(/\D/g, "");
    if (cnpjLimpo.length === 14) {
      return cnpjLimpo.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }
    return cnpj;
  };

  const formatarData = (data) => {
    if (!data) return "Não informado";
    return formatValue("data", data);
  };

  const formatarDataFinal = (data) => {
    if (!data) return "Não informado";
    try {
      const novaData = new Date(data);
      if (isNaN(novaData.getTime())) return "Não informado";
      
      // Ajustar fuso horário (mesma lógica do formatValue)
      novaData.setHours(novaData.getHours() + 3);
      const ISOdate = novaData.toISOString();
      const dataAjustada = new Date(ISOdate);
      
      // Formatar como DD/MM/YYYY garantindo que o dia seja exibido corretamente
      const dia = String(dataAjustada.getDate()).padStart(2, '0');
      const mes = String(dataAjustada.getMonth() + 1).padStart(2, '0');
      const ano = dataAjustada.getFullYear();
      
      return `${dia}/${mes}/${ano}`;
    } catch (error) {
      // Fallback para formatValue se houver erro
      return formatValue("data", data);
    }
  };

  const carregarDados = useCallback(async () => {
    if (!versaoIdFinal) {
      console.warn("⚠️ [ResumoGeral] Versão ID não encontrado");
      return;
    }

    setLoading(true);
    try {
      // 1. Buscar dados da versão completa
      const versaoCompleta = await buscarPreviaVersaoPisCofins(versaoIdFinal);
      setDadosVersaoCompleta(versaoCompleta);

      // 2. Extrair período para usar nas oportunidades
      // Tentar múltiplas fontes: versao.versao (JSONB), versao (direto), previa, dadosVersao (contexto)
      const versaoJsonb = versaoCompleta?.versao?.versao || versaoCompleta?.versao || {};
      const empresa = versaoJsonb.empresa || versaoJsonb.matriz || {};
      const periodo = versaoJsonb.periodo || versaoJsonb.competencia || {};
      
      // Extrair datas de múltiplas fontes (ordem de prioridade)
      const dtInicial = 
        periodo?.dtInicial || 
        periodo?.inicio || 
        versaoCompleta?.previa?.dtInicial ||
        dadosVersao?.periodo?.dtInicial ||
        dadosVersao?.dtInicial ||
        null;
      const dtFinal = 
        periodo?.dtFinal || 
        periodo?.fim || 
        versaoCompleta?.previa?.dtFinal ||
        dadosVersao?.periodo?.dtFinal ||
        dadosVersao?.dtFinal ||
        null;
      
      const dtInicialFormatada = formatarData(dtInicial);
      // Usar formatarDataFinal para garantir que o dia seja exibido corretamente
      const dtFinalFormatada = formatarDataFinal(dtFinal);
      
      // Formatar período: se tiver ambas as datas, usar formato "DD/MM/YYYY a DD/MM/YYYY"
      // Se tiver apenas uma, usar apenas ela
      // Se não tiver nenhuma, usar "-"
      const periodoFormatado = 
        dtInicialFormatada && dtFinalFormatada && dtInicialFormatada !== "Não informado" && dtFinalFormatada !== "Não informado"
          ? `${dtInicialFormatada} a ${dtFinalFormatada}`
          : dtInicialFormatada && dtInicialFormatada !== "Não informado"
          ? dtInicialFormatada
          : dtFinalFormatada && dtFinalFormatada !== "Não informado"
          ? dtFinalFormatada
          : "-";

      // 3. Buscar tipos de prévia da API
      const response = await buscarServicoPorTipo("PIS-COFINS");
      const tiposPreviaArray = response?.tipos || [];
      setTiposPrevia(tiposPreviaArray);

      // 4. Buscar valores salvos de TODOS os motores
      let valoresSalvosPorScriptTemp = {};
      try {
        const valoresResumo = await buscarValoresResumo(versaoIdFinal);
        if (valoresResumo?.sucesso && valoresResumo?.valores) {
          valoresSalvosPorScriptTemp = valoresResumo.valores;
        }
      } catch (error) {
        console.warn("⚠️ [ResumoGeral] Erro ao buscar valores salvos:", error);
        // Continuar sem valores salvos (usar valores zerados)
      }

      // 4.1 Para Sob Despesa (CONCILIACAO_SPED): usar totais da tabela de resumo "compõe projeto" se não houver valores salvos
      try {
        const scriptSobDespesa = "CONCILIACAO_SPED";
        const salvos = valoresSalvosPorScriptTemp[scriptSobDespesa];
        const temValoresSalvos = salvos && ((salvos.pis && salvos.pis > 0) || (salvos.cofins && salvos.cofins > 0));
        if (!temValoresSalvos) {
          const resTotais = await buscarTotaisCompoeConciliacaoSped(versaoIdFinal);
          if (resTotais?.sucesso && resTotais?.totais) {
            const { pis = 0, cofins = 0 } = resTotais.totais;
            valoresSalvosPorScriptTemp = {
              ...valoresSalvosPorScriptTemp,
              [scriptSobDespesa]: {
                pis,
                cofins,
                total: pis + cofins,
              },
            };
          }
        }
      } catch (errTotais) {
        console.warn("⚠️ [ResumoGeral] Erro ao buscar totais compoe conciliação SPED:", errTotais);
      }

      setValoresSalvosPorScript(valoresSalvosPorScriptTemp);

      // 5. Mapear tipos para oportunidades (sempre exibir todas)
      const oportunidades = tiposPreviaArray.map((tipo) => {
        const script = tipo.script; // "CONCILIACAO_SPED", "MONOFASICO", "EXCLUSOES", etc.
        const valoresSalvos = valoresSalvosPorScriptTemp[script];
        
        // Verificar se tem valores salvos para este script
        const temValoresSalvos = valoresSalvos && (
          (valoresSalvos.pis && valoresSalvos.pis > 0) || 
          (valoresSalvos.cofins && valoresSalvos.cofins > 0)
        );
        
        const isSobDespesa = tipo.descricao === "Sob Despesas" || tipo.descricao === "Sob Despesa";
        // O campo medida_judicial já contém o tipo: "Administrativo", "Judicial" ou "SUGESTAO"
        // Se for null/undefined, usar "Administrativo" como padrão
        const medidaJudicial = tipo.medida_judicial || null;
        const tipoOportunidade = medidaJudicial === "Judicial" ? "Judicial" : "Administrativo";
        
        // Usar valores salvos se existirem, caso contrário usar valores zerados
        const valorPis = valoresSalvos?.pis || 0;
        const valorCofins = valoresSalvos?.cofins || 0;
        const total = valoresSalvos?.total || (valorPis + valorCofins);
        
        // Determinar se a oportunidade está ativa (tem valores salvos)
        const isAtivo = temValoresSalvos;
        
        return {
          oportunidade: tipo.descricao,
          // Sempre usar o período formatado se disponível, independente de ter valores salvos
          periodoApurado: periodoFormatado && periodoFormatado !== "-" ? periodoFormatado : "-",
          fundamentoLegal: tipo.fundamentacao || "-",
          tipo: tipoOportunidade,
          valorPis,
          valorCofins,
          total,
          idTipoPrevia: tipo.idTipoPrevia,
          script: tipo.script,
          temValoresSalvos: isAtivo,
        };
      });

      // 6. Calcular totais baseados em TODAS as oportunidades com valores salvos
      const oportunidadesAtivas = oportunidades.filter((op) => 
        op.temValoresSalvos && (op.valorPis > 0 || op.valorCofins > 0)
      );

      const totalPis = oportunidadesAtivas.reduce((sum, op) => sum + (op.valorPis || 0), 0);
      const totalCofins = oportunidadesAtivas.reduce((sum, op) => sum + (op.valorCofins || 0), 0);
      const economiaTotal = totalPis + totalCofins;

      // Separar créditos administrativos e judiciais (apenas ativos)
      const creditosAdministrativos = oportunidadesAtivas
        .filter((op) => op.tipo === "Administrativo")
        .reduce((acc, op) => ({
          pis: acc.pis + (op.valorPis || 0),
          cofins: acc.cofins + (op.valorCofins || 0),
          total: acc.total + (op.total || 0),
        }), { pis: 0, cofins: 0, total: 0 });

      const creditosJudiciais = oportunidadesAtivas
        .filter((op) => op.tipo === "Judicial")
        .reduce((acc, op) => ({
          pis: acc.pis + (op.valorPis || 0),
          cofins: acc.cofins + (op.valorCofins || 0),
          total: acc.total + (op.total || 0),
        }), { pis: 0, cofins: 0, total: 0 });

      const resumoMock = {
        totalPis,
        totalCofins,
        economiaTotal,
        creditosAdministrativos,
        creditosJudiciais,
        oportunidades,
      };

      setDadosResumo(resumoMock);
    } catch (error) {
      console.error("❌ [ResumoGeral] Erro ao carregar dados:", error);
      setObjSnackBar({
        type: "error",
        msg: error.response?.data?.mensagem || "Erro ao carregar dados do resumo.",
      });
    } finally {
      setLoading(false);
    }
  }, [versaoIdFinal]);

  useEffect(() => {
    if (!versaoIdFinal) {
      console.error("❌ [ResumoGeral] ID da versão não encontrado");
      setObjSnackBar({
        type: "error",
        msg: "ID da versão não encontrado. Redirecionando...",
      });
      setTimeout(() => {
        navigate("/piscofins/previas");
      }, 2000);
      return;
    }

    carregarDados();
  }, [versaoIdFinal, carregarDados, navigate]);

  const handleVoltar = () => {
    const idPreviaPisCofins =
      dadosVersao?.idPreviaPisCofins ??
      previaInfo?.idPreviaPisCofins ??
      previaInfo?.idPrevia ??
      null;

    if (idPreviaPisCofins && typeof voltarParaListaComModalVersoes === "function") {
      // Usar função compartilhada do contexto para voltar com a modal de versões aberta
      voltarParaListaComModalVersoes(idPreviaPisCofins);
    } else {
      // Fallback seguro: apenas voltar para a lista
      navigate("/piscofins/previas");
    }
  };

  const handleExportarPDF = async () => {
    if (!dadosResumo || !dadosResumo.oportunidades || dadosResumo.oportunidades.length === 0) {
      setObjSnackBar({
        type: "error",
        msg: "Nenhum dado disponível para exportar.",
      });
      return;
    }

    setLoading(true);
    try {
      // Preparar informações do cabeçalho
      const versaoJsonb = dadosVersaoCompleta?.versao?.versao || {};
      const empresaObj = versaoJsonb.empresa || versaoJsonb.matriz || {};
      const periodo = versaoJsonb.periodo || versaoJsonb.competencia || {};
      
      // Calcular período formatado
      const dtInicial = 
        periodo?.dtInicial || 
        periodo?.inicio || 
        dadosVersaoCompleta?.previa?.dtInicial ||
        dadosVersao?.periodo?.dtInicial || 
        dadosVersao?.dtInicial || 
        null;
      const dtFinal = 
        periodo?.dtFinal || 
        periodo?.fim || 
        dadosVersaoCompleta?.previa?.dtFinal ||
        dadosVersao?.periodo?.dtFinal || 
        dadosVersao?.dtFinal || 
        null;
      const dtInicialFormatada = formatarData(dtInicial);
      const dtFinalFormatada = formatarDataFinal(dtFinal);
      const periodoFormatadoExport =
        dtInicialFormatada && dtFinalFormatada && dtInicialFormatada !== "Não informado" && dtFinalFormatada !== "Não informado"
          ? `${dtInicialFormatada} a ${dtFinalFormatada}`
          : dtInicialFormatada !== "Não informado" || dtFinalFormatada !== "Não informado"
          ? dtInicialFormatada !== "Não informado" ? dtInicialFormatada : dtFinalFormatada
          : "-";
      
      const infoCabecalho = {
        empresaNome:
          empresaObj.razaoSocial ||
          versaoJsonb.razaoSocial ||
          dadosVersaoCompleta?.previa?.razaoSocial ||
          dadosVersao?.nomeEmpresa ||
          "Empresa não informada",
        cnpjEmpresa:
          empresaObj.cnpj ||
          versaoJsonb.cnpj ||
          dadosVersaoCompleta?.previa?.cnpj ||
          dadosVersao?.cnpjMatriz ||
          dadosVersao?.cnpj ||
          "",
        periodoFormatado: periodoFormatadoExport,
        temasSelecionados: Array.isArray(dadosVersao?.temasSelecionados)
          ? dadosVersao.temasSelecionados.join(" • ")
          : dadosVersao?.temaSelecionado || "Resumo Geral",
      };

      // Exportar para PDF
      const resultadoExportacao = await exportarResumoGeralPDF({
        oportunidades: dadosResumo.oportunidades || [],
        totais: {
          totalPis: dadosResumo.totalPis || 0,
          totalCofins: dadosResumo.totalCofins || 0,
          economiaTotal: dadosResumo.economiaTotal || 0,
        },
        creditosAdministrativos: dadosResumo.creditosAdministrativos || {},
        creditosJudiciais: dadosResumo.creditosJudiciais || {},
        infoCabecalho,
        versaoId: versaoIdFinal,
        valoresSalvosPorScript,
        tiposPrevia: tiposPrevia || [],
      });

      if (resultadoExportacao?.sucesso) {
        setObjSnackBar({
          type: "success",
          msg: `Arquivo ${resultadoExportacao.nomeArquivo} exportado com sucesso!`,
        });
      }
    } catch (error) {
      console.error("Erro ao exportar para PDF:", error);
      setObjSnackBar({
        type: "error",
        msg: error?.message || "Erro ao exportar arquivo PDF. Por favor, tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportarExcel = async () => {
    if (!dadosResumo || !dadosResumo.oportunidades || dadosResumo.oportunidades.length === 0) {
      setObjSnackBar({
        type: "error",
        msg: "Nenhum dado disponível para exportar.",
      });
      return;
    }

    setLoading(true);
    try {
      // Preparar informações do cabeçalho
      const versaoJsonb = dadosVersaoCompleta?.versao?.versao || {};
      const empresaObj = versaoJsonb.empresa || versaoJsonb.matriz || {};
      const periodo = versaoJsonb.periodo || versaoJsonb.competencia || {};
      
      // Calcular período formatado
      const dtInicial = 
        periodo?.dtInicial || 
        periodo?.inicio || 
        dadosVersaoCompleta?.previa?.dtInicial ||
        dadosVersao?.periodo?.dtInicial || 
        dadosVersao?.dtInicial || 
        null;
      const dtFinal = 
        periodo?.dtFinal || 
        periodo?.fim || 
        dadosVersaoCompleta?.previa?.dtFinal ||
        dadosVersao?.periodo?.dtFinal || 
        dadosVersao?.dtFinal || 
        null;
      const dtInicialFormatada = formatarData(dtInicial);
      const dtFinalFormatada = formatarDataFinal(dtFinal);
      const periodoFormatadoExport =
        dtInicialFormatada && dtFinalFormatada && dtInicialFormatada !== "Não informado" && dtFinalFormatada !== "Não informado"
          ? `${dtInicialFormatada} a ${dtFinalFormatada}`
          : dtInicialFormatada !== "Não informado" || dtFinalFormatada !== "Não informado"
          ? dtInicialFormatada !== "Não informado" ? dtInicialFormatada : dtFinalFormatada
          : "-";
      
      const infoCabecalho = {
        empresaNome:
          empresaObj.razaoSocial ||
          versaoJsonb.razaoSocial ||
          dadosVersaoCompleta?.previa?.razaoSocial ||
          dadosVersao?.nomeEmpresa ||
          "Empresa não informada",
        cnpjEmpresa:
          empresaObj.cnpj ||
          versaoJsonb.cnpj ||
          dadosVersaoCompleta?.previa?.cnpj ||
          dadosVersao?.cnpjMatriz ||
          dadosVersao?.cnpj ||
          "",
        periodoFormatado: periodoFormatadoExport,
        temasSelecionados: Array.isArray(dadosVersao?.temasSelecionados)
          ? dadosVersao.temasSelecionados.join(" • ")
          : dadosVersao?.temaSelecionado || "Resumo Geral",
      };

      // Exportar para Excel
      const resultadoExportacao = await exportarResumoGeralExcel({
        oportunidades: dadosResumo.oportunidades || [],
        totais: {
          totalPis: dadosResumo.totalPis || 0,
          totalCofins: dadosResumo.totalCofins || 0,
          economiaTotal: dadosResumo.economiaTotal || 0,
        },
        creditosAdministrativos: dadosResumo.creditosAdministrativos || {},
        creditosJudiciais: dadosResumo.creditosJudiciais || {},
        tiposPrevia: tiposPrevia || [],
        infoCabecalho,
        versaoId: versaoIdFinal,
        valoresSalvosPorScript,
      });

      if (resultadoExportacao?.sucesso) {
        setObjSnackBar({
          type: "success",
          msg: `Arquivo ${resultadoExportacao.nomeArquivo} exportado com sucesso!`,
        });
      }
    } catch (error) {
      console.error("Erro ao exportar para Excel:", error);
      setObjSnackBar({
        type: "error",
        msg: error?.message || "Erro ao exportar arquivo Excel. Por favor, tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportarCsv = async () => {
    if (!versaoIdFinal) {
      setObjSnackBar({ type: "error", msg: "Versão não identificada." });
      return;
    }
    setLoading(true);
    try {
      const res = await exportarCsvPreviaPisCofins(versaoIdFinal);
      if (res?.nomeArquivo) {
        window.alert(`Arquivo ${res.nomeArquivo} salvo em Relatórios com sucesso!`);
      } else {
        window.alert(res?.mensagem || "Nenhum lançamento com \"compõe projeto\" para esta versão. Arquivo não gerado.");
      }
    } catch (error) {
      const msg = error?.response?.data?.error || error?.message || "Erro ao exportar CSV.";
      setObjSnackBar({ type: "error", msg });
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizarAnalise = async () => {
    const previa = dadosVersaoCompleta?.previa || {};
    const idPrevia =
      previaInfo?.idPreviaPisCofins ??
      previaInfo?.idPrevia ??
      previa.idPreviaPisCofins ??
      previa.id ??
      dadosVersao?.idPreviaPisCofins ??
      null;

    if (!idPrevia || !versaoIdFinal) {
      setObjSnackBar({
        type: "error",
        msg: "Dados da prévia ou versão não encontrados. Tente voltar e abrir o resumo novamente.",
      });
      return;
    }

    const statusAtual = previaInfo?.status ?? previa.status ?? null;
    const isProjetoAprovadoStatus =
      statusAtual === "PROJETO-APROVADO" || statusAtual === "PREVIA-CANCELADA";
    const isProjetoIniciadoStatus = statusAtual === "PROJETO-INICIADO";

    if (isProjetoAprovadoStatus) {
      return;
    }

    setLoadingFinalizar(true);
    try {
      if (isProjetoIniciadoStatus) {
        const response = await aprovarProjetoPisCofins(idPrevia, {
          versaoId: versaoIdFinal,
        });
        if (response?.sucesso || response?.message) {
          // O crédito é lançado só pelo backend (idempotente); aqui apenas refletimos o resultado.
          const pendentes = Number(response?.creditos?.pendentes) || 0;
          if (pendentes > 0) {
            setCreditosPendentes({ idPrevia, versaoId: versaoIdFinal, pendentes });
            setObjSnackBar({
              type: "warning",
              msg: "Projeto aprovado, mas alguns créditos não foram lançados na Conta Corrente.",
            });
          } else {
            setObjSnackBar({
              type: "success",
              msg: "Projeto aprovado e créditos aplicados em Conta Corrente.",
            });
            navigate("/piscofins/previas", { replace: true });
          }
        } else {
          setObjSnackBar({
            type: "error",
            msg: response?.mensagem || "Erro ao aprovar projeto.",
          });
        }
      } else {
        const response = await iniciarProjeto(idPrevia, versaoIdFinal);
        if (response?.sucesso) {
          setObjSnackBar({
            type: "success",
            msg: response.mensagem || "Projeto iniciado com sucesso! A prévia foi travada para análise.",
          });
          navigate("/piscofins/previas", { replace: true });
        } else {
          setObjSnackBar({
            type: "error",
            msg: response?.mensagem || "Erro ao iniciar projeto.",
          });
        }
      }
    } catch (error) {
      console.error("Erro ao finalizar análise (projeto):", error);
      const mensagem =
        error?.response?.data?.mensagem ||
        error?.response?.data?.message ||
        error?.message ||
        (isProjetoIniciadoStatus ? "Erro ao aprovar projeto." : "Erro ao iniciar projeto. Tente novamente.");
      setObjSnackBar({
        type: "error",
        msg: mensagem,
      });
    } finally {
      setLoadingFinalizar(false);
    }
  };

  if (loading && !dadosResumo) {
    return <Loading loading={true} />;
  }

  // Extrair informações da empresa e período
  // Tentar múltiplas fontes: versao.versao (JSONB), versao (direto), previa, dadosVersao (contexto)
  const versaoJsonb = dadosVersaoCompleta?.versao?.versao || dadosVersaoCompleta?.versao || {};
  const empresa = versaoJsonb.empresa || versaoJsonb.matriz || {};
  const periodo = versaoJsonb.periodo || versaoJsonb.competencia || {};
  const previa = dadosVersaoCompleta?.previa || {};
  
  const empresaNome =
    empresa.razaoSocial ||
    versaoJsonb.razaoSocial ||
    previa.razaoSocial || // PascalCase
    previa.razao_social || // snake_case (comum no backend)
    dadosVersao?.nomeEmpresa ||
    dadosVersao?.empresa ||
    "Empresa não informada";
  
  const cnpjEmpresa = formatarCnpj(
    empresa.cnpj ||
    versaoJsonb.cnpj ||
    previa.cnpj ||
    dadosVersao?.cnpjMatriz ||
    dadosVersao?.cnpj ||
    ""
  );

  // Extrair período para concatenar as datas
  // Tentar múltiplas fontes: versao.versao (JSONB), previa, dadosVersao (contexto)
  const dtInicial = 
    periodo?.dtInicial || 
    periodo?.inicio || 
    dadosVersaoCompleta?.previa?.dtInicial ||
    dadosVersao?.periodo?.dtInicial || 
    dadosVersao?.dtInicial || 
    null;
  const dtFinal = 
    periodo?.dtFinal || 
    periodo?.fim || 
    dadosVersaoCompleta?.previa?.dtFinal ||
    dadosVersao?.periodo?.dtFinal || 
    dadosVersao?.dtFinal || 
    null;
  const dtInicialFormatada = formatarData(dtInicial);
  // Usar formatarDataFinal para garantir que o dia seja exibido corretamente
  const dtFinalFormatada = formatarDataFinal(dtFinal);
  const periodoFormatado =
    dtInicialFormatada && dtFinalFormatada && dtInicialFormatada !== "Não informado" && dtFinalFormatada !== "Não informado"
      ? `${dtInicialFormatada} a ${dtFinalFormatada}`
      : dtInicialFormatada !== "Não informado" || dtFinalFormatada !== "Não informado"
      ? dtInicialFormatada !== "Não informado" ? dtInicialFormatada : dtFinalFormatada
      : "-";

  const temasSelecionados = Array.isArray(dadosVersao?.temasSelecionados)
    ? dadosVersao.temasSelecionados.join(" • ")
    : dadosVersao?.temaSelecionado || "Resumo Geral";

  // Definir texto e estado do botão de finalização conforme o status da prévia
  const previaStatus = dadosVersaoCompleta?.previa || {};
  const statusPrevia = previaInfo?.status ?? previaStatus.status ?? null;
  const isProjetoIniciado = statusPrevia === "PROJETO-INICIADO";
  const isProjetoAprovado =
    statusPrevia === "PROJETO-APROVADO" || statusPrevia === "PREVIA-CANCELADA";

  let textoBotaoFinalizacao = "Confirmar Projeto";
  if (isProjetoIniciado) {
    textoBotaoFinalizacao = "Aprovar Projeto";
  } else if (isProjetoAprovado) {
    textoBotaoFinalizacao = "Projeto Aprovado";
  }

  const botaoFinalizacaoDesabilitado =
    loading || loadingFinalizar || isProjetoAprovado || Boolean(creditosPendentes);

  const exibirBotaoFinalizacao =
    isProjetoAprovado ||
    (isProjetoIniciado && pode(CHAVES_PIS_COFINS_APPROVE)) ||
    (!isProjetoIniciado && !isProjetoAprovado && pode(CHAVES_PIS_COFINS_EDIT));

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span>Empresa</span>
            <strong>{empresaNome}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>CNPJ</span>
            <strong>{cnpjEmpresa}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>Período</span>
            <strong>{periodoFormatado}</strong>
          </div>
          <div className={styles.infoItem}>
            <span>Temas selecionados</span>
            <strong>{temasSelecionados}</strong>
          </div>
        </div>

        {dadosResumo && (
          <>
            {/* Cards de Totais */}
            <div className={styles.totalsRow}>
              <div className={`${styles.totalCard} ${styles.totalPis}`}>
                <span>Total PIS</span>
                <strong>{formatCurrency(dadosResumo.totalPis)}</strong>
              </div>
              <div className={`${styles.totalCard} ${styles.totalCofins}`}>
                <span>Total COFINS</span>
                <strong>{formatCurrency(dadosResumo.totalCofins)}</strong>
              </div>
              <div className={`${styles.totalCard} ${styles.totalEconomia}`}>
                <span>Economia Total</span>
                <strong>{formatCurrency(dadosResumo.economiaTotal)}</strong>
              </div>
            </div>

            {/* Distribuição por Tipo de Crédito */}
            <div className={styles.distributionGrid}>
              <div className={styles.distributionCard}>
                <h3 className={styles.distributionCardTitle}>💼 Créditos Administrativos</h3>
                <div className={styles.distributionItem}>
                  <span className={styles.distributionLabel}>PIS</span>
                  <span className={`${styles.distributionValue} ${styles.distributionValuePis}`}>
                    {formatCurrency(dadosResumo.creditosAdministrativos.pis)}
                  </span>
                </div>
                <div className={styles.distributionItem}>
                  <span className={styles.distributionLabel}>COFINS</span>
                  <span className={`${styles.distributionValue} ${styles.distributionValueCofins}`}>
                    {formatCurrency(dadosResumo.creditosAdministrativos.cofins)}
                  </span>
                </div>
                <div className={`${styles.distributionItem} ${styles.distributionItemTotal}`}>
                  <span className={styles.distributionLabelTotal}>Total</span>
                  <span className={styles.distributionValueTotal}>
                    {formatCurrency(dadosResumo.creditosAdministrativos.total)}
                  </span>
                </div>
              </div>

              <div className={styles.distributionCard}>
                <h3 className={styles.distributionCardTitle}>⚖️ Créditos Judiciais</h3>
                <div className={styles.distributionItem}>
                  <span className={styles.distributionLabel}>PIS</span>
                  <span className={`${styles.distributionValue} ${styles.distributionValuePis}`}>
                    {formatCurrency(dadosResumo.creditosJudiciais.pis)}
                  </span>
                </div>
                <div className={styles.distributionItem}>
                  <span className={styles.distributionLabel}>COFINS</span>
                  <span className={`${styles.distributionValue} ${styles.distributionValueCofins}`}>
                    {formatCurrency(dadosResumo.creditosJudiciais.cofins)}
                  </span>
                </div>
                <div className={`${styles.distributionItem} ${styles.distributionItemTotal}`}>
                  <span className={styles.distributionLabelTotal}>Total</span>
                  <span className={styles.distributionValueTotal}>
                    {formatCurrency(dadosResumo.creditosJudiciais.total)}
                  </span>
                </div>
              </div>
            </div>

            {/* Botões de Exportação */}
            {pode(CHAVES_PIS_COFINS_EXPORT) && (
            <div className={styles.filtrosContainer}>
              <div className={styles.filtrosRight}>
                <button
                  type="button"
                  onClick={handleExportarPDF}
                  className={styles.botaoExportar}
                  disabled={loading}
                >
                  <DownloadIcon style={{ fontSize: "1rem", marginRight: "0.5rem" }} />
                  <span>Exportar PDF</span>
                </button>
                <button
                  type="button"
                  onClick={handleExportarExcel}
                  className={styles.botaoExportar}
                  disabled={loading}
                >
                  <DownloadIcon style={{ fontSize: "1rem", marginRight: "0.5rem" }} />
                  <span>Exportar Excel</span>
                </button>
                <button
                  type="button"
                  onClick={handleExportarCsv}
                  className={styles.botaoExportar}
                  disabled={loading}
                >
                  <DownloadIcon style={{ fontSize: "1rem", marginRight: "0.5rem" }} />
                  <span>Exportar CSV</span>
                </button>
              </div>
            </div>
            )}

            {/* Tabela de Oportunidades */}
            <TableContainer component={Paper} className={styles.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Oportunidade</TableCell>
                    <TableCell>Período Apurado</TableCell>
                    <TableCell>Fundamento Legal / Tese Judicial</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell align="right">Valor PIS (R$)</TableCell>
                    <TableCell align="right">Valor COFINS (R$)</TableCell>
                    <TableCell align="right">Total (R$)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dadosResumo.oportunidades?.map((oportunidade, index) => {
                    // Verificar se tem valores salvos (oportunidade ativa)
                    const temValoresSalvos = oportunidade.temValoresSalvos || false;
                    const isDesabilitada = !temValoresSalvos;
                    // Fundo cinza apenas para linhas que NÃO têm valores salvos E têm tipo "Administrativo" ou "Judicial"
                    const temFundoCinza = isDesabilitada && (oportunidade.tipo === "Administrativo" || oportunidade.tipo === "Judicial");
                    // Fundo branco para linhas com valores salvos
                    const temFundoBranco = temValoresSalvos;
                    
                    return (
                      <TableRow 
                        key={index}
                        className={
                          temFundoBranco 
                            ? styles.rowFundoBranco 
                            : temFundoCinza 
                            ? styles.rowFundoCinza 
                            : ""
                        }
                      >
                        <TableCell className={isDesabilitada ? styles.textoOpaco : ""}>
                          <strong>{oportunidade.oportunidade}</strong>
                        </TableCell>
                        <TableCell className={isDesabilitada ? styles.textoOpaco : ""}>
                          {isDesabilitada ? "-" : oportunidade.periodoApurado}
                        </TableCell>
                        <TableCell className={`${styles.teseText} ${isDesabilitada ? styles.textoOpaco : ""}`}>
                          {oportunidade.fundamentoLegal.split("\n").map((linha, i) => (
                            <div key={i}>{linha}</div>
                          ))}
                        </TableCell>
                        <TableCell className={isDesabilitada ? styles.textoOpaco : ""}>
                          <Chip
                            label={oportunidade.tipo}
                            size="small"
                            color={oportunidade.tipo === "Administrativo" ? "primary" : "secondary"}
                          />
                        </TableCell>
                        <TableCell align="right" className={`${styles.valorPis} ${isDesabilitada ? styles.textoOpaco : ""}`}>
                          {formatCurrency(oportunidade.valorPis)}
                        </TableCell>
                        <TableCell align="right" className={`${styles.valorCofins} ${isDesabilitada ? styles.textoOpaco : ""}`}>
                          {formatCurrency(oportunidade.valorCofins)}
                        </TableCell>
                        <TableCell align="right" className={isDesabilitada ? styles.textoOpaco : ""}>
                          <strong>{formatCurrency(oportunidade.total)}</strong>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {dadosResumo.oportunidades?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Nenhuma oportunidade encontrada
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                {dadosResumo && (
                  <tfoot>
                    <TableRow className={styles.tableFooter}>
                      <TableCell colSpan={4}>
                        <strong>Total Geral</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(dadosResumo.totalPis)}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(dadosResumo.totalCofins)}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(dadosResumo.economiaTotal)}</strong>
                      </TableCell>
                    </TableRow>
                  </tfoot>
                )}
              </Table>
            </TableContainer>

            {creditosPendentes && (
              <AvisoCreditosPendentes
                pendentes={creditosPendentes.pendentes}
                onReenviar={async () =>
                  (
                    await reenviarCreditosPisCofins(creditosPendentes.idPrevia, {
                      versaoId: creditosPendentes.versaoId,
                    })
                  )?.creditos
                }
                onFechar={() => {
                  setCreditosPendentes(null);
                  navigate("/piscofins/previas", { replace: true });
                }}
              />
            )}

            {/* Footer */}
            <div className={styles.footer}>
              <div className={styles.footerLeft}>
                <WhiteButton textValue="Voltar" clickFunction={handleVoltar} />
              </div>
              <div className={styles.footerRight}>
                {exibirBotaoFinalizacao && (
                <BlueButton
                  textValue={textoBotaoFinalizacao}
                  clickFunction={handleFinalizarAnalise}
                  disabled={botaoFinalizacaoDesabilitado}
                />
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </div>
  );
};

export default ResumoGeral;
